/**
 * Phase 27c — Master console mailer health-check banner.
 *
 * Polls ``GET /api/health/mailer`` on mount and every 5 minutes,
 * caches the result in local state (no context — one banner instance
 * per page). Renders a dismissible warning row when the health-check
 * reports ``ok: false``, with extra copy when
 * ``domain_unverified`` is true (the helloquanta.app prod incident
 * shape).
 *
 * Silent otherwise — no green "everything OK" banner, no layout jump
 * on the happy path.
 *
 * In-session dismiss only. Refresh (or next login) re-fetches and
 * re-renders the banner if the mailer is still broken.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { MailWarning, RefreshCw, X } from "lucide-react";

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min

export default function MasterMailerHealthBanner() {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/health/mailer");
      setHealth(data);
    } catch (e) {
      // If the endpoint itself 5xxes (shouldn't since it's caught
      // server-side, but be defensive), surface a synthetic banner.
      setHealth({
        ok: false,
        driver: "unknown",
        error: `Health-check endpoint failed: ${e?.message || "unknown"}`,
        checked_at: new Date().toISOString(),
      });
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (dismissed) return null;
  if (!health) return null;         // first paint — stay silent
  if (health.ok !== false) return null;  // healthy or skipped-dev-mode

  const domainUnverified = !!health.domain_unverified;
  const domain = health.from_domain || "unknown";
  const from = health.from || "unset";

  return (
    <Alert
      variant="destructive"
      className="border-amber-500/50 bg-amber-500/10 text-amber-900"
      data-testid="master-mailer-health-banner"
    >
      <MailWarning className="h-4 w-4 text-amber-800" />
      <AlertDescription className="ml-6 space-y-2">
        <div className="text-sm font-semibold text-amber-900">
          {domainUnverified
            ? `Mailer is misconfigured — sender domain \`${domain}\` is not verified in Resend.`
            : "Mailer is misconfigured — outgoing email is not being delivered."}
        </div>
        <div className="text-xs italic text-amber-900/80">
          {domainUnverified
            ? "No emails are being delivered. Update MAIL_FROM / PLATFORM_MAIL_FROM to use a domain verified in the Resend dashboard, then redeploy."
            : "Live smoke send failed. See /api/health/mailer for the raw error."}
        </div>
        <div className="rounded-md border border-amber-500/30 bg-white/60 p-2 font-mono text-[11px] leading-relaxed text-amber-950">
          <div>
            <span className="text-amber-800/70">driver:</span> {health.driver || "unknown"}
          </div>
          <div>
            <span className="text-amber-800/70">from:</span> {from}
          </div>
          {health.error && (
            <div className="mt-1 break-all">
              <span className="text-amber-800/70">error:</span> {health.error}
            </div>
          )}
          <div className="mt-1">
            <span className="text-amber-800/70">checked_at:</span> {health.checked_at}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={refreshing}
            data-testid="master-mailer-banner-refresh"
          >
            <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : ""}`} />
            Recheck
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDismissed(true)}
            data-testid="master-mailer-banner-dismiss"
          >
            <X className="h-3 w-3" /> Dismiss
          </Button>
          <a
            href="/api/health/mailer"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs underline decoration-dotted text-amber-900/80 hover:text-amber-900"
            data-testid="master-mailer-banner-raw-link"
          >
            View raw JSON →
          </a>
        </div>
      </AlertDescription>
    </Alert>
  );
}
